/** Explicit property/project confirmation using shared store-scoped API contracts. */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useShopifyTrackedMutation } from '../../hooks/useShopifyTrackedMutation'
import { trackShopifyAction } from '../../shopifyUiTracking'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { shopifyIntegrationPropertiesSchema, type ShopifyIntegrationProperties } from '@testfeed/integration-contracts'
import { useShopifyAppContext, shopifyQueryKey } from '../../ShopifyAppContext'
import { shopifyApiFetch, type ShopifyTokenSource } from '../../shopifyApiFetch'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { resolveMerchantErrorMessage } from '../../merchantErrors'

const copy = shopifyComponentCopy.integrationsPage
async function readResponse(response: Response) {
  const value: unknown = await response.json()
  if (!response.ok) {
    const failure = value as { error?: string }
    throw new Error(failure.error ?? copy.propertiesFailed)
  }
  return shopifyIntegrationPropertiesSchema.parse(value)
}
type Props = {
  shopify: ShopifyTokenSource
  onClose: () => void
  onSaved: () => void
  initialSourceId?: string
  setupId?: string
}
export function ExistingPropertyPanel(props: Props) {
  const scope = useShopifyAppContext()
  const query = useQuery({
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['integration-properties']),
    queryFn: async () => {
      const result = await readResponse(await shopifyApiFetch(props.shopify, '/api/integrations/properties'))
      if (result.organizationId !== scope.orgId || result.storeConnectionId !== scope.connectionId)
        throw Error('SHOPIFY_INTEGRATIONS_SCOPE_CHANGED')
      return result
    },
    enabled: !!scope.orgId && !!scope.connectionId,
    ...shopifyQueryPolicies.integrations,
  })
  let content
  if (query.isPending) content = <s-text>{copy.loadProperties}</s-text>
  else if (query.error)
    content = <s-banner tone="critical">{resolveMerchantErrorMessage(query.error, copy.propertiesFailed)}</s-banner>
  else content = <PropertyConfirmation {...props} data={query.data} />
  return (
    <s-section heading={copy.chooseProperty}>
      <s-button onClick={() => trackShopifyAction(props.shopify, 'close-provider', props.onClose)}>
        {copy.close}
      </s-button>
      {content}
    </s-section>
  )
}
function PropertyConfirmation({
  data,
  shopify,
  onSaved,
  initialSourceId,
  setupId,
}: Props & { data: ShopifyIntegrationProperties }) {
  const scope = useShopifyAppContext()
  const [sourceId, setSourceId] = useState(initialSourceId ?? '')
  const [projectId, setProjectId] = useState(() => {
    const preferred = data.projects.find((row) => row.isDefault)
    if (preferred) return preferred.projectId
    if (data.projects.length === 1) return data.projects[0].projectId
    return ''
  })
  const current = data.relationships.find((row) => row.projectId === projectId && row.sourceConnectionId === sourceId)
  const mutation = useShopifyTrackedMutation(shopify, 'link-property', {
    mutationFn: async () =>
      readResponse(
        await shopifyApiFetch(shopify, '/api/integrations/properties', {
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              operation: 'save-link',
              ...(setupId ? { setupId } : {}),
              projectId,
              sourceConnectionId: sourceId,
              expectedRevision: current?.revision ?? 0,
              expectedRelationshipId: current?.id ?? null,
            }),
          },
        }),
      ),
    onSuccess: onSaved,
  })
  let notice = null
  if (!data.projects.length)
    notice = (
      <s-stack gap="base">
        <s-text>{copy.missingProject}</s-text>
        <s-button
          href={data.projectSetupUrl}
          target="_blank"
          disabled={!data.projectSetupUrl}
          onClick={() => trackShopifyAction(shopify, 'open-provider-setup', () => undefined)}
        >
          {copy.chooseProject}
        </s-button>
      </s-stack>
    )
  else if (!data.properties.length) notice = <s-text>{copy.missingProperty}</s-text>
  let collectionNotice = null
  if (setupId) collectionNotice = <s-text>{copy.newCollectionExplanation}</s-text>
  let failure = null
  if (mutation.error)
    failure = <s-banner tone="critical">{resolveMerchantErrorMessage(mutation.error, copy.propertiesFailed)}</s-banner>
  const blocked =
    !data.projects.some((row) => row.projectId === projectId) ||
    !data.properties.some((row) => row.connectionId === sourceId) ||
    mutation.isPending
  return (
    <s-stack direction="block" gap="base">
      <s-text>{copy.linkExplanation}</s-text>
      <s-text>{scope.shopDomain}</s-text>
      {notice}
      {collectionNotice}
      <s-select
        label={copy.chooseProperty}
        value={sourceId}
        onChange={(event) =>
          trackShopifyAction(shopify, 'select-property', () => setSourceId(event.currentTarget.value))
        }
      >
        <s-option value="">{copy.chooseProperty}</s-option>
        {data.properties.map((row) => (
          <s-option key={row.connectionId} value={row.connectionId}>
            {row.label}
          </s-option>
        ))}
      </s-select>
      <s-select
        label={copy.chooseProject}
        value={projectId}
        onChange={(event) =>
          trackShopifyAction(shopify, 'select-project', () => setProjectId(event.currentTarget.value))
        }
      >
        <s-option value="">{copy.chooseProject}</s-option>
        {data.projects.map((row) => (
          <s-option key={row.projectId} value={row.projectId}>
            {row.name}
          </s-option>
        ))}
      </s-select>
      {failure}
      <s-button variant="primary" disabled={blocked} loading={mutation.isPending} onClick={() => mutation.mutate()}>
        {copy.confirmLink}
      </s-button>
    </s-stack>
  )
}
